import type { IntlShape } from 'react-intl';

import {
  AttributesField,
  CustomAttributes,
  FormField,
  MultiSelectField,
  ShortResponseField,
  SingleSelectField,
} from '~/types/SSOForm';

export const getFieldErrors = (formField: FormField<any>, intl: IntlShape) => {
  let parsedFormField;
  const errors = [];
  const fieldRequired = intl.formatMessage({
    id: 'FormSectionUtils.fieldRequired',
    defaultMessage: 'This field is required.',
    description: 'Error message when a required field is empty',
  });

  switch (formField.field.type) {
    case 'SHORT_RESPONSE':
    case 'LONG_RESPONSE':
      parsedFormField = formField as FormField<ShortResponseField>;
      const fieldValue = parsedFormField.field.value;
      const pattern = new RegExp(parsedFormField.pattern ?? '', 'i');
      const isPatternMatch = parsedFormField.pattern
        ? pattern.test(fieldValue)
        : true;

      if (parsedFormField.required && fieldValue === '') {
        errors.push(fieldRequired);
      }
      if (!isPatternMatch && fieldValue) {
        errors.push(
          intl.formatMessage({
            id: 'FormSectionUtils.patternMismatch',
            defaultMessage:
              'The value entered does not match the required pattern.',
            description:
              'Error message when field value does not match required pattern',
          }),
        );
      }

      break;

    case 'SINGLE_SELECT':
    case 'ROLE_SELECT':
      parsedFormField = formField as FormField<SingleSelectField>;
      if (
        parsedFormField.required &&
        parsedFormField.field?.value?.value === ''
      ) {
        errors.push(fieldRequired);
      }
      break;

    case 'MULTI_SELECT':
      parsedFormField = formField as FormField<MultiSelectField>;
      if (
        parsedFormField.required &&
        parsedFormField.field?.value?.length === 0
      ) {
        errors.push(
          intl.formatMessage({
            id: 'FormSectionUtils.multiSelectRequired',
            defaultMessage: 'At least one option must be selected.',
            description:
              'Error message when multi-select field requires at least one option',
          }),
        );
      }
      break;
    // ATTRIBUTES: will have its own validation on the specific component
  }

  return errors;
};

export const isFieldValid = (formField: FormField<any>) => {
  switch (formField.field.type) {
    case 'SHORT_RESPONSE':
    case 'LONG_RESPONSE':
      const longResponseField = formField as FormField<ShortResponseField>;
      const fieldValue = longResponseField.field.value;
      const pattern = new RegExp(longResponseField.pattern ?? '', 'i');
      const isPatternMatch = longResponseField.pattern
        ? pattern.test(fieldValue)
        : true;

      if (longResponseField.required) {
        return !!fieldValue && isPatternMatch;
      } else if (fieldValue) {
        return isPatternMatch;
      } else {
        return true;
      }
    case 'SINGLE_SELECT':
      const singleSelectField = formField as FormField<SingleSelectField>;
      if (singleSelectField.required) {
        return !!singleSelectField.field?.value?.value;
      } else {
        return true;
      }
    case 'MULTI_SELECT':
      const multiSelectField = formField as FormField<MultiSelectField>;
      if (multiSelectField.required) {
        return multiSelectField.field?.value?.length > 0;
      } else {
        return true;
      }

    case 'ATTRIBUTES':
      const attributesField = formField as FormField<AttributesField>;
      if (attributesField.required) {
        const { email, firstName, lastName } = attributesField.field.value;
        return !!email && !!firstName && !!lastName && !!email;
      } else {
        return true;
      }

    case 'CUSTOM_ATTRIBUTES':
      const customAttributesField = formField as FormField<CustomAttributes>;

      const isSomeNameEmpty = Object.keys(
        customAttributesField.field.value,
      ).some((name) => !name);
      const isSomeLabelEmpty = Object.values(
        customAttributesField.field.value,
      ).some((label) => !label);

      return !isSomeLabelEmpty && !isSomeNameEmpty;

    default:
      return true;
  }
};
